import { FC, MouseEventHandler } from 'react';
import { Link, Path } from 'react-router-dom';
import cx from 'classnames';
import styles from './Button.module.scss';

export type Size = 'small' | 'medium' | 'large';

export interface ButtonProps {
  active?: boolean;
  className?: string;
  disabled?: boolean;
  /** Link outside the app using <a />.
   * MUST NOT be used with `to`.
   */
  href?: string;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  size?: Size;
  /** Link inside the application using react-router.
   * MUST NOT be used with `href`.
   */
  to?: string | Partial<Path>;
  stopPropagation?: boolean;
  type?: 'button' | 'submit';
  secondary?: boolean;
}

const Button: FC<ButtonProps> = ({ active, className, children, disabled, href, size = 'medium', to, stopPropagation, onClick, type, secondary, ...props }) => {
  if (to && href) {
    throw new Error('`href` and `to` passed');
  }

  const sharedProps = {
    ...props,
    className: cx(className, styles.button, styles[size], { [styles.active]: active, [styles.disabled]: disabled, [styles.secondary]: secondary }),
  };

  const onButtonClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (onClick) {
      onClick(e);
    }
  };

  if (to) {
    return (
      <Link onClick={onButtonClick} {...sharedProps} to={to}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a {...sharedProps} href={href} onClick={onButtonClick} target=" _blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  return (
    <button {...sharedProps} onClick={onButtonClick} disabled={disabled} type={type === 'submit' ? 'submit' : 'button'}>
      {children}
    </button>
  );
};

export default Button;
